import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-4-3b：三位數 × 一位數（四上進階版，含多次進位）。
 * 四上第三章：三位 × 一位進階，強化進位計算。
 *
 * difficulty 1：400–799 × 3–6（中等乘積）
 * difficulty 2：500–899 × 5–8（多次進位）
 * difficulty 3：700–999 × 6–9（大數多次進位）
 */
export function generateMul3x1Up(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  let aMin: number, aMax: number, bMin: number, bMax: number;
  if (difficulty === 1) {
    [aMin, aMax, bMin, bMax] = [400, 799, 3, 6];
  } else if (difficulty === 2) {
    [aMin, aMax, bMin, bMax] = [500, 899, 5, 8];
  } else {
    [aMin, aMax, bMin, bMax] = [700, 999, 6, 9];
  }

  const a = randInt(rand, aMin, aMax);
  const b = randInt(rand, bMin, bMax);
  const correct = a * b;

  const onesProduct = (a % 10) * b;
  const tensProduct = (Math.floor(a / 10) % 10) * b;
  const hundProduct = Math.floor(a / 100) * b;
  const carry1 = Math.floor(onesProduct / 10);
  const tensPlusCarry = tensProduct + carry1;

  // Distractors
  const distractors = new Set<number>();
  const noCarry = hundProduct * 100 + tensProduct * 10 + (onesProduct % 10);
  if (noCarry !== correct && noCarry > 0) distractors.add(noCarry);
  distractors.add(correct + 10);
  if (correct - 10 > 0) distractors.add(correct - 10);

  while (distractors.size < 3) {
    const offset = randInt(rand, 1, 100) * (rand() > 0.5 ? 1 : -1);
    const c = correct + offset;
    if (c > 0 && c !== correct && !distractors.has(c)) distractors.add(c);
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  const shuffled = [...optionList].sort((x, y) => mulberry32(seed + x)() - mulberry32(seed + y)());

  return {
    kind: 'mcq',
    prompt: `${a} × ${b} = ?`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `個位 ${a % 10}×${b}=${onesProduct}；十位 ${Math.floor(a / 10) % 10}×${b}+進位${carry1}=${tensPlusCarry}；百位 ${Math.floor(a / 100)}×${b}+進位${Math.floor(tensPlusCarry / 10)}=${hundProduct + Math.floor(tensPlusCarry / 10)} → ${correct}`,
    kp: 'math.mul.3x1.4up',
  };
}
